"use client";

import { useCallback, useRef, useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide } from "@/components/optilase/carousel-page";
import { onSurface, type Surface } from "@/components/optilase/surface";
import { useScrollNeedsFocus } from "@/lib/use-scrollable";
import { cn } from "@/lib/utils";

export type DocumentCard = {
  id: string;
  title?: string;
  description?: string;
  meta?: string;
  cover?: { filename: string; alt: string; focus?: string } | null;
  href?: string | null;
  buttonLabel?: string;
};

export type DocumentCardsViewProps = {
  cards: DocumentCard[];
  columns?: string;
  label?: string;
  surface?: Surface | null;
  cardSurface?: Surface | null;
};

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 1.5v9m0 0L4.5 7M8 10.5 11.5 7M2 12.5v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function DocumentCardsView({
  cards,
  columns,
  label,
  surface,
  cardSurface,
}: DocumentCardsViewProps) {
  const cardOn = cardSurface ?? surface;
  const scroller = useRef<HTMLUListElement>(null);
  const isScrollable = useScrollNeedsFocus(scroller);
  const [page, setPage] = useState(0);
  const sync = useCallback(() => {
    const el = scroller.current;
    if (el) setPage(activeSlide(el));
  }, []);

  if (cards.length === 0) return null;

  const cols = columns === "two" ? "lg:grid-cols-2" : "lg:grid-cols-2 xl:grid-cols-3";

  return (
    <div className="flex flex-col">
      <ul
        ref={scroller}
        onScroll={sync}
        tabIndex={isScrollable ? 0 : -1}
        role={isScrollable ? "region" : undefined}
        aria-label={isScrollable ? label || "Documents" : undefined}
        className={cn(
          "-mx-5 flex snap-x snap-mandatory scroll-pl-5 scrollbar-none gap-4 overflow-x-auto px-5 pb-2",
          "md:-mx-10 md:scroll-pl-10 md:px-10",
          "lg:mx-0 lg:grid lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0",
          cols,
        )}
      >
        {cards.map((card) => {
          const href = card.href && card.href !== "#" ? card.href : null;
          const buttonLabel = (card.buttonLabel || "").trim() || "Download PDF";
          const body = (
            <>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <h3
                  className={cn(
                    "text-h6 font-semibold tracking-[-0.01em] text-balance",
                    onSurface(cardOn, "text-white", "text-grey-09", "text-grey-09"),
                  )}
                >
                  {card.title}
                </h3>
                {card.description && (
                  <p
                    className={cn(
                      "text-base leading-relaxed tracking-[-0.01em]",
                      onSurface(cardOn, "text-white/85", "text-grey-08", "text-grey-08"),
                    )}
                  >
                    {card.description}
                  </p>
                )}
                {card.meta && (
                  <p
                    className={cn(
                      "text-sm leading-snug font-medium",
                      onSurface(cardOn, "text-white/70", "text-grey-06", "text-grey-06"),
                    )}
                  >
                    {card.meta}
                  </p>
                )}
                {href && (
                  <span className="text-teal-08 mt-auto inline-flex items-center gap-2 pt-3 font-bold whitespace-nowrap group-hover:underline">
                    {buttonLabel}
                    <DownloadIcon />
                  </span>
                )}
              </div>

              <span
                aria-hidden
                className={cn(
                  "w-px shrink-0 self-stretch",
                  onSurface(cardOn, "bg-white/20", "bg-grey-01", "bg-grey-01"),
                )}
              />

              <div className="bg-teal-01 relative aspect-3/4 w-16 shrink-0 self-center overflow-hidden rounded-lg ring-1 ring-black/5 sm:w-20 lg:w-24">
                {card.cover?.filename ? (
                  <AssetImage
                    src={card.cover.filename}
                    focus={card.cover.focus}
                    alt={card.cover.alt || card.title || ""}
                    fill
                    sizes="(min-width: 1115px) 96px, (min-width: 640px) 80px, 64px"
                    className="object-cover object-top"
                  />
                ) : null}
              </div>
            </>
          );

          const shell = cn(
            "flex h-full gap-3 rounded-2xl p-4 sm:p-5 lg:gap-5 lg:p-6",
            cardSurface ? "" : "border border-grey-01",
          );
          const style = cardSurface ? { backgroundColor: cardSurface.backgroundColor } : undefined;

          return (
            <li key={card.id} className="w-[86%] shrink-0 snap-start sm:w-auto">
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "group",
                    shell,
                    cardSurface ? "" : "hover:border-teal-04 transition-colors",
                  )}
                  style={style}
                >
                  {body}
                </a>
              ) : (
                <div className={shell} style={style}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {cards.length > 1 && (
        <CarouselDots
          count={cards.length}
          active={page}
          label="document"
          onSelect={(i) => {
            const card = scroller.current?.children[i] as HTMLElement | undefined;
            card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
          }}
          className="lg:hidden"
        />
      )}
    </div>
  );
}
